/* ------------------------ Functional imports start ------------------------ */
import Cookies from 'js-cookie'
import React, { createContext, useContext, useEffect, useState } from 'react'

import LogTool from './logger/logTools'
import { fetchCurrentUser } from './api/userApi'
/* ------------------------- Functional imports end ------------------------- */

export interface WebSocketContextValue {
  socket: WebSocket | null
  setIsLoggedIn: (isLoggedIn: boolean) => void
  setAccessTokenWS: (accessToken: string) => void
}

export const WebSocketContext = createContext<WebSocketContextValue>({
  socket: null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setIsLoggedIn: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setAccessTokenWS: () => {},
})

export const useWebSocket = () => useContext(WebSocketContext)

type Props = {
  children: React.ReactNode
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */

export const WebSocketProvider = (props: Props) => {
  /* -------------------------- Non state data start -------------------------- */
  const { children } = props
  const log = new LogTool({ context: 'WebSocketProvider', enable: true, logLevel: 'warn' })
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false)
  const [accessTokenWS, setAccessTokenWS] = useState<string>('')
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  const [socket, setSocket] = useState<WebSocket | null>(null)
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  useEffect(() => {
    void (async () => {
      await checkCurrentUser()
    })()
  }, [])

  useEffect(() => {
    const accessToken = Cookies.get('access') || accessTokenWS
    if (isLoggedIn && accessToken) {
      const apiUrl: string | undefined = process.env.REACT_APP_WS_URL
      if (!apiUrl) {
        throw Error("API url is not defined. Please add the the URL via the REACT_APP_API_URL environment variable")
      }
      const webSocketUrl = apiUrl + `v1/accounts/users/self/?token=${accessToken}`
      const newSocket = new WebSocket(
        webSocketUrl
      )
      setSocket(newSocket)

      newSocket.onopen = () => {
        log.info('WebSocket connected')
      }

      newSocket.onclose = () => {
        log.info('WebSocket disconnected')
        setSocket(null)
      }
    } else if (socket) {
      socket.close()
      setSocket(null)
    } else {
      log.info('Not logged in')
      setSocket(null)
    }

    return () => {
      if (socket) {
        socket.close()
      }
    }
  }, [isLoggedIn])
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */
  async function checkCurrentUser() {
    log.info('Begin fetching current user')
    try {
      await fetchCurrentUser()
      log.info('Success fetching current user')
      setIsLoggedIn(true)
    } catch (error) {
      log.error('Error fetching current user', error)
      setIsLoggedIn(false)
    }
  }
  /* -------------------------- Utility functions end ------------------------- */

  return (
    <WebSocketContext.Provider value={{ socket, setIsLoggedIn, setAccessTokenWS }}>
      {children}
    </WebSocketContext.Provider>
  )
}
